import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';

const LegalPoliciesPage = () => {
  const [activePolicy, setActivePolicy] = useState('returns');
  const location = useLocation();
  
  useEffect(() => {
    // Check for policy parameter in URL
    const queryParams = new URLSearchParams(location.search);
    const policyParam = queryParams.get('policy');
    
    if (policyParam && policies[policyParam]) {
      setActivePolicy(policyParam);
    }
  }, [location]);

  // Update document title when active policy changes
  useEffect(() => {
    document.title = `${policies[activePolicy].title} | Master Collectors Forge`;
  }, [activePolicy]);

  const policies = {
    returns: {
      title: 'Return Policy',
      content: `
        <h3>Return Policy</h3>
        <p>At Master Collectors Forge, we want you to be completely satisfied with your purchase. We offer a straightforward return policy to ensure your collecting experience is positive.</p>
        
        <h4>Return Eligibility</h4>
        <ul>
          <li>Items must be returned within 30 days of delivery</li>
          <li>Items must be in their original, unopened packaging</li>
          <li>Proof of purchase (order number or receipt) is required</li>
          <li>Collectible items must be returned in their original sealed packaging</li>
          <li>Custom or personalized items are not eligible for return unless defective</li>
        </ul>
        
        <h4>Return Process</h4>
        <ol>
          <li>Contact our customer service team at returns@mastercollectorsforge.com to initiate a return</li>
          <li>You will receive a Return Merchandise Authorization (RMA) number</li>
          <li>Package the item securely in appropriate shipping materials</li>
          <li>Include the RMA number on the outside of the package</li>
          <li>Ship the item to the address provided by our customer service team</li>
          <li>We recommend using a tracked shipping method</li>
        </ol>
        
        <h4>Shipping Costs</h4>
        <p>Return shipping costs are the responsibility of the customer unless the return is due to our error (wrong item shipped, defective product, etc.).</p>
        
        <h4>Exceptions</h4>
        <p>Certain items are not eligible for return due to their nature:</p>
        <ul>
          <li>Digital products</li>
          <li>Items with broken seals or tampered packaging</li>
          <li>Special order items</li>
          <li>Items marked as final sale</li>
        </ul>
      `
    },
    refunds: {
      title: 'Refund & Dispute Policy',
      content: `
        <h3>Refund & Dispute Policy</h3>
        <p>Master Collectors Forge is committed to fair and transparent refund practices. We process refunds promptly once we've received and inspected returned items.</p>
        
        <h4>Refund Timeline</h4>
        <ul>
          <li>Returned items are typically inspected within 3-5 business days of receipt</li>
          <li>Approved refunds are processed within 1-2 business days after inspection</li>
          <li>Credit card refunds may take an additional 5-10 business days to appear on your statement</li>
          <li>Store credit refunds are issued immediately upon approval</li>
        </ul>
        
        <h4>Refund Methods</h4>
        <p>Refunds will be issued using the original payment method when possible:</p>
        <ul>
          <li>Credit/debit card purchases will be refunded to the original card</li>
          <li>PayPal purchases will be refunded to the PayPal account</li>
          <li>Store credit or gift card purchases will be refunded as store credit</li>
        </ul>
        
        <h4>Partial Refunds</h4>
        <p>Partial refunds may be issued in the following circumstances:</p>
        <ul>
          <li>Item returned shows signs of handling or use</li>
          <li>Item packaging is damaged or incomplete</li>
          <li>Only part of an order is returned</li>
        </ul>
        
        <h4>Dispute Resolution</h4>
        <p>If you believe your refund was incorrectly processed or denied:</p>
        <ol>
          <li>Contact our customer service team at disputes@mastercollectorsforge.com</li>
          <li>Include your order number and details about the issue</li>
          <li>We will review your case within 5 business days</li>
          <li>If we cannot resolve the dispute directly, we offer mediation through a third-party service</li>
        </ol>
      `
    },
    cancellations: {
      title: 'Cancellation Policy',
      content: `
        <h3>Cancellation Policy</h3>
        <p>We understand that circumstances change. Our cancellation policy is designed to be fair while allowing us to efficiently process orders.</p>
        
        <h4>Order Cancellation Window</h4>
        <ul>
          <li>Orders can be cancelled within 24 hours of placement without penalty</li>
          <li>Orders that have not yet shipped can typically be cancelled for a full refund</li>
          <li>Orders that have been shipped cannot be cancelled (please refer to our Return Policy)</li>
        </ul>
        
        <h4>How to Cancel an Order</h4>
        <ol>
          <li>Log into your account and view your recent orders</li>
          <li>Select the order you wish to cancel</li>
          <li>Click the "Request Cancellation" button if available</li>
          <li>If the button is not available, contact customer service immediately</li>
        </ol>
        
        <h4>Special Orders & Pre-Orders</h4>
        <p>Special orders and pre-orders have specific cancellation terms:</p>
        <ul>
          <li>Pre-orders can be cancelled anytime before the item ships</li>
          <li>Special orders may be subject to a restocking fee of 10-20% if cancelled after processing has begun</li>
          <li>Limited edition items may have non-refundable deposits</li>
        </ul>
        
        <h4>Subscription Cancellations</h4>
        <p>For subscription-based services:</p>
        <ul>
          <li>Monthly subscriptions can be cancelled at any time before the next billing cycle</li>
          <li>Annual subscriptions can be cancelled but may not be eligible for partial refunds</li>
          <li>Cancellations take effect at the end of the current billing period</li>
        </ul>
      `
    },
    restrictions: {
      title: 'Legal & Export Restrictions',
      content: `
        <h3>Legal & Export Restrictions</h3>
        <p>Master Collectors Forge complies with all applicable laws regarding the sale and shipping of collectible items. Please be aware of the following restrictions.</p>
        
        <h4>Age Restrictions</h4>
        <ul>
          <li>Customers must be 18 years or older to purchase from our website</li>
          <li>Some premium collectibles may require age verification upon delivery</li>
        </ul>
        
        <h4>Domestic Shipping Restrictions</h4>
        <p>Certain items may have shipping restrictions within the United States:</p>
        <ul>
          <li>High-value shipments may require signature confirmation</li>
          <li>Some items cannot be shipped to P.O. boxes</li>
          <li>Certain states may have restrictions on specific collectible products</li>
        </ul>
        
        <h4>International Shipping & Export Restrictions</h4>
        <p>International customers should be aware of the following:</p>
        <ul>
          <li>We ship to select countries only</li>
          <li>Customers are responsible for all import duties, taxes, and customs fees</li>
          <li>Some countries prohibit the import of certain collectibles</li>
          <li>We comply with all U.S. export regulations and sanctions programs</li>
          <li>Orders may be cancelled if they violate export restrictions</li>
        </ul>
        
        <h4>Authenticity & Provenance</h4>
        <p>For certain historical or rare collectibles:</p>
        <ul>
          <li>Proper documentation of provenance may be required for specific items</li>
          <li>Some items may have restrictions based on cultural heritage protection laws</li>
          <li>All items are guaranteed authentic as described</li>
        </ul>
      `
    },
    promotions: {
      title: 'Promotion Terms & Conditions',
      content: `
        <h3>Promotion Terms & Conditions</h3>
        <p>From time to time, Master Collectors Forge offers special promotions, discounts, and sales events. All promotions are subject to the following terms and conditions.</p>
        
        <h4>General Promotion Terms</h4>
        <ul>
          <li>All promotions have a defined start and end date/time</li>
          <li>Promotions cannot be combined unless explicitly stated</li>
          <li>We reserve the right to modify or end promotions early</li>
          <li>Promotional discounts apply only to eligible items as specified</li>
          <li>Limit one promotion per customer/household unless otherwise stated</li>
        </ul>
        
        <h4>Discount Codes</h4>
        <p>When using discount or coupon codes:</p>
        <ul>
          <li>Codes must be entered at checkout to apply</li>
          <li>Codes are case-sensitive and must be entered exactly as provided</li>
          <li>Codes cannot be applied retroactively to orders already placed</li>
          <li>Some products may be excluded from discount code promotions</li>
        </ul>
        
        <h4>Free Shipping Promotions</h4>
        <ul>
          <li>Free shipping offers typically apply to standard shipping only</li>
          <li>Minimum purchase thresholds may apply</li>
          <li>International shipping may be excluded from free shipping promotions</li>
          <li>Oversized or heavy items may incur additional shipping charges</li>
        </ul>
        
        <h4>Loyalty Program & Rewards</h4>
        <p>For our Collector's Circle loyalty program:</p>
        <ul>
          <li>Points are earned on qualifying purchases only</li>
          <li>Points have no cash value and cannot be transferred</li>
          <li>Points expire 12 months after issuance if unused</li>
          <li>Reward redemptions are subject to availability</li>
          <li>Accounts inactive for 12+ months may have points forfeited</li>
        </ul>
      `
    }
  };

  return (
    <PageContainer>
      <Container>
        <PageHeader>
          <PageTitle>Legal Policies</PageTitle>
          <PageDescription>
            Our commitment to transparency and fair business practices. Please review our policies below.
          </PageDescription>
        </PageHeader>
        
        <PolicyContainer>
          <PolicyNav>
            {Object.entries(policies).map(([key, policy]) => (
              <PolicyTab 
                key={key}
                $active={activePolicy === key}
                onClick={() => setActivePolicy(key)}
              >
                {policy.title}
              </PolicyTab>
            ))}
          </PolicyNav>
          
          <PolicyContent>
            <div dangerouslySetInnerHTML={{ __html: policies[activePolicy].content }} />
          </PolicyContent>
        </PolicyContainer>
      </Container>
    </PageContainer>
  );
};

// Styled Components
const PageContainer = styled.div`
  padding: 120px 0 80px;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 1rem;
  color: ${({ theme }) => theme.colors.text.dark};
`;

const PageDescription = styled.p`
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.text.muted};
  max-width: 700px;
  margin: 0 auto;
`;

const PolicyContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: white;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.md};
  overflow: hidden;
`;

const PolicyNav = styled.div`
  display: flex;
  flex-wrap: wrap;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: column;
  }
`;

const PolicyTab = styled.button`
  padding: 1rem 1.5rem;
  background-color: ${({ $active, theme }) => 
    $active ? theme.colors.primary : 'transparent'};
  color: ${({ $active, theme }) => 
    $active ? 'white' : theme.colors.text.dark};
  border: none;
  font-weight: ${({ $active }) => ($active ? '600' : '400')};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.short};
  
  &:hover {
    background-color: ${({ $active, theme }) => 
      $active ? theme.colors.primary : theme.colors.background.light};
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    text-align: left;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    
    &:last-child {
      border-bottom: none;
    }
  }
`;

const PolicyContent = styled.div`
  padding: 2rem;
  
  h3 {
    font-size: 1.75rem;
    margin-bottom: 1.5rem;
    color: ${({ theme }) => theme.colors.text.dark};
  }
  
  h4 {
    font-size: 1.25rem;
    margin: 1.5rem 0 1rem;
    color: ${({ theme }) => theme.colors.text.dark};
  }
  
  p {
    margin-bottom: 1rem;
    line-height: 1.6;
  }
  
  ul, ol {
    margin-bottom: 1.5rem;
    padding-left: 1.5rem;
    
    li {
      margin-bottom: 0.5rem;
      line-height: 1.5;
    }
  }
`;

export default LegalPoliciesPage;
